import { useState, useEffect } from "react";
import { ScrollFade } from "@/components/ScrollFade";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Gift, Copy, Link2, MousePointerClick, UserPlus, Sparkles, BadgeCheck,
  Wallet, Clock, Landmark, CreditCard,
} from "lucide-react";

const eur = (c) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((c || 0) / 100);

const STATUS = {
  pending: { label: "Estimée (en gel)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  available: { label: "Disponible", cls: "bg-green-50 text-green-700 border-green-200" },
  paid: { label: "Versée", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  reversed: { label: "Annulée (remboursement)", cls: "bg-red-50 text-red-600 border-red-200" },
};

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 mb-2">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-serif text-3xl text-ink">{value}</p>
    </div>
  );
}

export default function Parrainage() {
  const [d, setD] = useState(null);

  const load = () => api.get("/affiliate/dashboard").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!d) return <div className="p-12 text-slate-400" data-testid="affiliate-loading">Chargement…</div>;

  const refLink = `${window.location.origin}/?ref=${d.code}`;
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Lien de parrainage copié."); };
  const isMember = d.payout_method === "credit_cotisation";
  const pct = d.threshold_cents > 0 ? Math.min(100, Math.round((d.earnings.available_cents / d.threshold_cents) * 100)) : 100;

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="affiliate-dashboard">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Gift className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Programme de parrainage</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-2">Votre tableau de bord affilié</h1>
      <p className="text-slate-500 mb-8">
        {Math.round(d.config.rate_year1 * 100)} % la première année, puis {Math.round(d.config.rate_after * 100)} %, sur le montant HT encaissé. Attribution au 1<sup>er</sup> clic (fenêtre 60 jours).
      </p>

      {/* Lien */}
      <div className="bg-ink text-white rounded-lg p-6 mb-8 max-w-3xl">
        <div className="flex items-center gap-2 text-coral mb-1"><Link2 className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Votre lien personnel</span></div>
        <div className="flex items-center gap-2 mt-3">
          <input data-testid="affiliate-link" readOnly value={refLink}
            className="flex-1 bg-white/10 border border-white/15 rounded-sm px-3.5 py-2.5 text-sm text-white outline-none" />
          <button data-testid="copy-link-btn" onClick={copy}
            className="flex items-center gap-2 bg-coral-strong text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">
            <Copy className="h-4 w-4" /> Copier
          </button>
        </div>
      </div>

      {/* Stats parcours */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={MousePointerClick} label="Clics" value={d.stats.clicks} testid="stat-clicks" />
        <Stat icon={UserPlus} label="Inscrits" value={d.stats.signups} testid="stat-signups" />
        <Stat icon={Sparkles} label="En essai" value={d.stats.trials} testid="stat-trials" />
        <Stat icon={BadgeCheck} label="Clients actifs" value={d.stats.active_clients} testid="stat-active" />
      </div>

      {/* Gains */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div data-testid="earn-pending" className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-amber-600 mb-2"><Clock className="h-3.5 w-3.5" /> Estimées (en gel 30 j)</div>
          <p className="font-serif text-3xl text-ink">{eur(d.earnings.pending_cents)}</p>
          <p className="text-xs text-slate-400 mt-1">Sécurisées après la fenêtre de remboursement.</p>
        </div>
        <div data-testid="earn-available" className="bg-white border-2 border-coral rounded-lg p-6">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-coral-dark mb-2"><Wallet className="h-3.5 w-3.5" /> Disponibles</div>
          <p className="font-serif text-3xl text-ink">{eur(d.earnings.available_cents)}</p>
          <p className="text-xs text-slate-400 mt-1">Prêtes à être {isMember ? "créditées" : "versées"}.</p>
        </div>
        <div data-testid="earn-paid" className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
            {isMember ? <CreditCard className="h-3.5 w-3.5" /> : <Landmark className="h-3.5 w-3.5" />} {isMember ? "Créditées" : "Versées"}
          </div>
          <p className="font-serif text-3xl text-ink">{eur(d.earnings.paid_cents)}</p>
          <p className="text-xs text-slate-400 mt-1">Cumul historique.</p>
        </div>
      </div>

      {/* Barre de progression vers le règlement */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-10 max-w-3xl" data-testid="payout-progress">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-ink">
            {isMember ? "Crédit sur cotisation" : "Prochain virement"}, seuil {eur(d.threshold_cents)}
          </span>
          <span className="text-slate-500">{eur(d.earnings.available_cents)} / {eur(d.threshold_cents)}</span>
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-coral-strong transition-all" style={{ width: `${pct}%` }} data-testid="payout-bar" />
        </div>
        <p className="text-sm text-coral-dark font-semibold mt-3" data-testid="payout-remaining">
          {d.remaining_to_payout_cents > 0
            ? `Encore ${eur(d.remaining_to_payout_cents)} avant votre prochain ${isMember ? "crédit" : "virement"}.`
            : `Seuil atteint, ${isMember ? "crédit" : "virement"} déclenchable par l'équipe APOLLO.`}
        </p>
      </div>

      {/* Commissions */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-10" data-testid="commissions-card">
        <h2 className="font-serif text-xl text-ink mb-4">Détail des commissions</h2>
        {d.commissions.some((c) => c.is_test) && (
          <p className="text-xs text-slate-400 mb-3" data-testid="test-note">Les lignes <strong>TEST</strong> proviennent du simulateur de démonstration et sont exclues de vos totaux réels.</p>
        )}
        {d.commissions.length === 0 ? (
          <p className="text-sm text-slate-400">Aucune commission pour l'instant. Partagez votre lien pour commencer.</p>
        ) : (
          <ScrollFade>
            <table className="w-full text-sm" data-testid="commissions-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4">Filleul</th><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Taux</th>
                  <th className="py-2 pr-4 text-right">Net</th><th className="py-2 pr-4">Statut</th><th className="py-2">Dispo. le</th>
                </tr>
              </thead>
              <tbody>
                {d.commissions.map((c) => {
                  const s = STATUS[c.status] || STATUS.pending;
                  return (
                    <tr key={c.id} className="border-b border-slate-50">
                      <td className="py-2.5 pr-4 text-slate-700">
                        {c.referred_name}
                        {c.is_test && <span className="ml-2 text-[10px] font-bold bg-slate-800 text-white px-1.5 py-0.5 rounded-full" data-testid="commission-test-badge">TEST</span>}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-400 text-xs">{(c.payment_date || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-4 text-slate-500">{Math.round(c.rate * 100)}%{c.is_first_year ? " · 1re année" : ""}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold text-ink">{eur(c.net_cents)}</td>
                      <td className="py-2.5 pr-4"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span></td>
                      <td className="py-2.5 text-slate-400 text-xs">{(c.available_at || "").slice(0, 10)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollFade>
        )}
      </div>

      {/* Historique règlements */}
      {d.payouts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="payouts-card">
          <h2 className="font-serif text-xl text-ink mb-4">Historique des règlements</h2>
          <div className="space-y-2">
            {d.payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-sm px-3 py-2">
                <span className="text-slate-500">{(p.created_at || "").slice(0, 10)}</span>
                <span className="text-xs font-semibold bg-slate-100 px-2 py-0.5 rounded-full">{p.method === "credit_cotisation" ? "Crédit cotisation" : "Virement"}</span>
                <span className="font-semibold text-ink">{eur(p.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
